import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faArrowLeft, faInfo } from "@fortawesome/free-solid-svg-icons";
import API from "../api/axios";

export default function CyclesScroller({
  onCycleSelect = () => {},
  initialSelectedCycle,
}) {
  const containerRef = useRef(null);
  const cycleRefs = useRef({});

  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [activeCycle, setActiveCycle] = useState(null);
  const [winnerVotesRequire, setWinnerVotesRequire] = useState(0);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  // Fetch settings for winner votes requirement
  useEffect(() => {
    API.get("/settings/")
      .then((res) => {
        const winner_votes_require = res.data.find(
          (item) => item.key === "winner_votes"
        );
        if (winner_votes_require) {
          setWinnerVotesRequire(Number(winner_votes_require.value));
        }
      })
      .catch((err) => {
        console.error("Error fetching settings:", err);
      });
  }, []);

  // Fetch cycles
  useEffect(() => {
    setLoading(true);
    API.get("/getcycle")
      .then((res) => {
        const allCycles = res.data?.data || [];
        console.log("Fetched cycles:", allCycles); // Debug log
        
        setCycles(allCycles);
        
        // Find active cycle
        const active = allCycles.find(cycle => cycle.status === 'active');
        console.log("Active cycle:", active); // Debug log
        setActiveCycle(active);
        
        // Set initial selected cycle with better logic
        let initialSelection = null;
        
        if (initialSelectedCycle) {
          // Try to find the initial cycle by ID
          initialSelection = allCycles.find(cycle => 
            cycle.id === initialSelectedCycle.id || 
            cycle.id === Number(initialSelectedCycle.id) ||
            String(cycle.id) === String(initialSelectedCycle.id)
          );
        }
        
        // Fallback to active cycle, then first cycle
        const finalSelection = initialSelection || active || allCycles[0] || null;
        console.log("Selected cycle:", finalSelection); // Debug log
        
        setSelectedCycle(finalSelection);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching cycles:", err);
        setLoading(false);
      });
  }, [initialSelectedCycle]);

  // Handle cycle selection changes
  useEffect(() => {
    if (selectedCycle) {
      console.log("Calling onCycleSelect with:", selectedCycle); // Debug log
      onCycleSelect(selectedCycle);
    }
  }, [selectedCycle, onCycleSelect]);

  // Auto-scroll to selected cycle
  useEffect(() => {
    if (selectedCycle && cycleRefs.current[selectedCycle.id] && cycles.length > 0) {
      // Add a small delay to ensure rendering is complete
      setTimeout(() => {
        cycleRefs.current[selectedCycle.id]?.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }, 100);
    }
  }, [cycles, selectedCycle]);

  // Countdown timer for active cycle
  useEffect(() => {
    if (!activeCycle?.ends_at) return;

    const updateCountdown = () => {
      const now = dayjs();
      const endTime = dayjs(activeCycle.ends_at);
      const diff = endTime.diff(now);

      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return false; // Stop timer
      } else {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((diff / (1000 * 60)) % 60);
        const seconds = Math.floor((diff / 1000) % 60);

        setCountdown({ days, hours, minutes, seconds });
        return true; // Continue timer
      }
    };

    // Initial update
    const shouldContinue = updateCountdown();
    if (!shouldContinue) return;

    const timer = setInterval(() => {
      const shouldContinue = updateCountdown();
      if (!shouldContinue) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [activeCycle]);

  const scroll = (direction) => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({
      left: direction === "left" ? -150 : 150,
      behavior: "smooth",
    });
  };

  // Extract cycle number from cycle name (e.g., "cycle_1_aug_2025" -> "1")
  const getCycleNumber = (cycleName) => {
    if (!cycleName) return "";
    const match = cycleName.match(/cycle_(\d+)_/);
    return match ? match[1] : cycleName;
  };

  const handleCycleClick = (cycle) => {
    console.log("Cycle clicked:", cycle); // Debug log
    setSelectedCycle(cycle);
  };

  const getCycleDateRange = (cycle) => {
    if (!cycle?.starts_at || !cycle?.ends_at) {
      return { start: "", end: "", startDate: null, endDate: null };
    }

    const startDate = dayjs(cycle.starts_at);
    const endDate = dayjs(cycle.ends_at);

    return {
      start: startDate.format("MMM D"),
      end: endDate.format("MMM D, YYYY"),
      startDate,
      endDate,
    };
  };

  const getSelectedCycleDateRange = () => {
    if (!selectedCycle) return { start: "", end: "" };
    return getCycleDateRange(selectedCycle);
  };

  const { start, end } = getSelectedCycleDateRange();

  // Show loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100px" }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show empty state
  if (cycles.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No cycles available</p>
      </div>
    );
  }

  
  return (
    <>
      <div className="d-flex align-items-center mb-3" id="weeks-scroller">
        <button className="arrow-btn me-2" onClick={() => scroll("left")}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>

        <div
          className="flex-grow-1 overflow-auto d-flex"
          style={{ whiteSpace: "nowrap" }}
          ref={containerRef}
        >
          {cycles.map((cycle) => {
            const isActive = cycle.status === 'active';
            const isSelected = selectedCycle && cycle.id === selectedCycle.id;
            const isFuture = !isActive && !cycle.winner_project_ids;
            const cycleNumber = getCycleNumber(cycle.name);

            return (
              <div
                key={cycle.id}
                ref={(el) => (cycleRefs.current[cycle.id] = el)}
                className={`week-item px-3 py-1 mx-1 ${
                  isSelected ? "active-week" : ""
                } `}
                onClick={() => handleCycleClick(cycle)}
                style={{ cursor: "pointer" }}
              >
                Cycle {cycleNumber}
              </div>
            );
          })}
        </div>

        <button className="arrow-btn ms-2" onClick={() => scroll("right")}>
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>

      <div className="display-selected-week">
        <div className="selected-week-txt">
          <p className="font-instrument">
            {selectedCycle ? `Cycle ${getCycleNumber(selectedCycle.name)}` : "No Cycle Selected"}
          </p>
          <p className="fst-normal small mb-2">
            {start} - {end}
          </p>

          {selectedCycle && selectedCycle.status === 'active' && 
           (countdown.days > 0 ||
            countdown.hours > 0 ||
            countdown.minutes > 0 ||
            countdown.seconds > 0) && (
            <p className="mb-2">
              ⏳ {countdown.days}d {countdown.hours}h {countdown.minutes}m{" "}
              {countdown.seconds}s left
            </p>
          )}

        </div>
        <button 
          type="button" 
          className="icon-info"
        >
          <FontAwesomeIcon icon={faInfo} />
          <span className="custom-tooltip">
            {`Min. ${winnerVotesRequire} votes required to win`}
          </span>
        </button>
      </div>
    </>
  );
}